import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLanguage } from '../contexts/LanguageContext';
import { analyzeFood, analyzeFoodImage, addFoodEntry } from '../lib/api';
import { Tag, Camera, Loader2, Search, Coins, Flame, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function guessMealType() {
    const h = new Date().getHours();
    if (h < 10) return 'breakfast';
    if (h < 14) return 'lunch';
    if (h < 17) return 'snack';
    return 'dinner';
}

export function PriceCheckSheet({ open, onOpenChange, onLogged }) {
    const { language } = useLanguage();
    const de = language === 'de';
    const [text, setText] = useState('');
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState(null);
    const [frequent, setFrequent] = useState([]);
    const [logging, setLogging] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setResult(null);
        setText('');
        axios.get(`${API}/nutrition/frequent-meals`)
            .then(res => setFrequent((res.data?.items || []).slice(0, 6)))
            .catch(() => setFrequent([]));
    }, [open]);

    const quoteAndShow = async (meal) => {
        const res = await axios.post(`${API}/taler/quote`, {
            calories: meal.calories || 0,
            protein: meal.protein || 0,
            carbs: meal.carbs || 0,
            fat: meal.fat || 0,
            fiber: meal.fiber || 0,
        });
        setResult({ ...meal, quote: res.data });
    };

    const checkText = async () => {
        if (!text.trim()) return;
        setChecking(true);
        try {
            const res = await analyzeFood({ food_name: text.trim() });
            const d = res.data;
            await quoteAndShow({ name: d.name || text.trim(), calories: d.calories, protein: d.protein, carbs: d.carbs, fat: d.fat, fiber: d.fiber });
        } catch (e) {
            toast.error(de ? 'Analyse fehlgeschlagen' : 'Analysis failed');
        } finally {
            setChecking(false);
        }
    };

    const checkPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setChecking(true);
        try {
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const res = await analyzeFoodImage(base64);
            const d = res.data;
            await quoteAndShow({ name: d.name || (de ? 'Mahlzeit' : 'Meal'), calories: d.calories, protein: d.protein, carbs: d.carbs, fat: d.fat, fiber: d.fiber });
        } catch (err) {
            toast.error(de ? 'Foto-Analyse fehlgeschlagen' : 'Photo analysis failed');
        } finally {
            setChecking(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const logNow = async () => {
        if (!result) return;
        setLogging(true);
        try {
            await addFoodEntry({
                name: result.name,
                meal_type: result.meal_type || guessMealType(),
                calories: Math.round(result.calories || 0),
                protein: result.protein || 0,
                carbs: result.carbs || 0,
                fat: result.fat || 0,
                fiber: result.fiber || 0,
                notes: de ? 'Preis-Check' : 'Price check',
            });
            const q = result.quote;
            toast.success(`${result.name} ✓ (${Math.round(result.calories)} kcal${q?.taler_enabled ? ` · ${q.taler} Taler` : ''})`);
            onOpenChange(false);
            onLogged?.();
        } catch (e) {
            toast.error(de ? 'Loggen fehlgeschlagen' : 'Logging failed');
        } finally {
            setLogging(false);
        }
    };

    const q = result?.quote;
    const talerMode = q?.taler_enabled;
    const after = talerMode ? q.taler_remaining_after : q?.kcal_remaining_after;
    const overAfter = (after ?? 0) < 0;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[88vh] overflow-y-auto" data-testid="price-check-sheet">
                <SheetHeader className="pb-2">
                    <SheetTitle className="flex items-center gap-2 text-base">
                        <Tag className="w-5 h-5 text-amber-600" />
                        {de ? 'Preis-Check – erst schauen, dann essen' : 'Price check – look before you eat'}
                    </SheetTitle>
                </SheetHeader>

                {!result && (
                    <div className="space-y-4 pb-4">
                        <p className="text-xs text-muted-foreground">
                            {de ? 'Hier wird nichts geloggt – du siehst nur, was es kosten würde.' : "Nothing gets logged here – you just see what it would cost."}
                        </p>
                        <div className="flex gap-2">
                            <Input
                                placeholder={de ? 'z.B. Cordon bleu mit Pommes' : 'e.g. schnitzel with fries'}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && checkText()}
                                disabled={checking}
                                data-testid="price-check-text-input"
                            />
                            <Button onClick={checkText} disabled={checking || !text.trim()} className="shrink-0 gap-1.5" data-testid="price-check-analyze-btn">
                                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                {de ? 'Prüfen' : 'Check'}
                            </Button>
                        </div>

                        <Button variant="outline" className="w-full gap-2" onClick={() => fileRef.current?.click()} disabled={checking} data-testid="price-check-photo-btn">
                            <Camera className="w-4 h-4" />
                            {de ? 'Foto machen & prüfen' : 'Take photo & check'}
                        </Button>
                        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={checkPhoto} />

                        {frequent.length > 0 && (
                            <div className="space-y-1.5" data-testid="price-check-frequent-list">
                                <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                                    {de ? 'Deine üblichen Mahlzeiten' : 'Your usual meals'}
                                </p>
                                {frequent.map((it) => (
                                    <button
                                        key={`${it.name}-${it.meal_type}`}
                                        onClick={() => quoteAndShow(it).catch(() => toast.error(de ? 'Fehler' : 'Error'))}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border bg-background hover:border-amber-400/60 hover:bg-amber-50/40 transition-colors text-left"
                                        data-testid={`price-check-frequent-${it.name.replace(/\s+/g, '-').toLowerCase()}`}
                                    >
                                        <span className="text-sm truncate mr-2">{it.name}</span>
                                        <span className="text-[11px] text-muted-foreground shrink-0">
                                            {it.calories} kcal<span className="text-amber-700 font-semibold"> · {it.taler} T</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                        {checking && (
                            <p className="text-xs text-muted-foreground flex items-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                {de ? 'Nanouk schätzt die Nährwerte…' : 'Nanouk is estimating the nutrition…'}
                            </p>
                        )}
                    </div>
                )}

                {result && q && (
                    <div className="space-y-3 pb-4" data-testid="price-check-result">
                        <div className={`rounded-2xl border-2 p-4 text-center ${overAfter ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50/60'}`}>
                            <p className="text-sm font-semibold truncate" data-testid="price-check-result-name">{result.name}</p>
                            {talerMode ? (
                                <>
                                    <p className="text-4xl font-bold text-amber-700 mt-1" data-testid="price-check-taler-price">
                                        {q.taler} <span className="text-lg">Taler</span>
                                    </p>
                                    {q.quality_taler !== 0 && (
                                        <p className={`text-[11px] font-medium ${q.quality_taler > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                            {q.quality_taler > 0
                                                ? `+${q.quality_taler} ${de ? 'Qualitäts-Aufschlag' : 'quality surcharge'}`
                                                : `${q.quality_taler} ${de ? 'Protein/Ballaststoff-Rabatt' : 'protein/fiber discount'}`}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="text-4xl font-bold text-amber-700 mt-1" data-testid="price-check-kcal-price">
                                    {Math.round(result.calories)} <span className="text-lg">kcal</span>
                                </p>
                            )}
                            <div className="flex items-center justify-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5" />{Math.round(result.calories)} kcal</span>
                                <span>P {Math.round(result.protein || 0)}g</span>
                                <span>C {Math.round(result.carbs || 0)}g</span>
                                <span>F {Math.round(result.fat || 0)}g</span>
                            </div>
                        </div>

                        <div className={`rounded-xl px-3 py-2.5 text-sm font-medium flex items-center gap-2 ${overAfter ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`} data-testid="price-check-budget-impact">
                            <Coins className="w-4 h-4 shrink-0" />
                            {talerMode ? (
                                overAfter
                                    ? (de ? `Das überzieht dein Konto um ${Math.abs(after)} Taler ⚠️` : `This overdraws your account by ${Math.abs(after)} Taler ⚠️`)
                                    : (de ? `Danach bleiben dir noch ${after} Taler ✅` : `You'd still have ${after} Taler left ✅`)
                            ) : (
                                overAfter
                                    ? (de ? `Das wären ${Math.abs(after)} kcal über deinem Tagesziel ⚠️` : `That's ${Math.abs(after)} kcal over your daily goal ⚠️`)
                                    : (de ? `Danach bleiben dir noch ${after} kcal ✅` : `You'd still have ${after} kcal left ✅`)
                            )}
                        </div>

                        <div className="flex gap-2">
                            <Button className="flex-1 gap-1.5" onClick={logNow} disabled={logging} data-testid="price-check-log-btn">
                                {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                {de ? 'Jetzt loggen' : 'Log it'}
                            </Button>
                            <Button variant="outline" className="flex-1 gap-1.5" onClick={() => setResult(null)} disabled={logging} data-testid="price-check-decline-btn">
                                <X className="w-4 h-4" />
                                {de ? 'Lieber nicht' : 'Rather not'}
                            </Button>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
